import { EMPTY_OBJECT, isEqual, isNullish } from '@ox/blox';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ApiRequest<TMethod extends (...args: any[]) => any = (...args: any[]) => any> {
  abortable?: boolean;
  autoRequestPayload?: Parameters<TMethod>[0];
  cacheable?: boolean; // only valid for the life of the component (i.e. once it's unmounted the cache is cleared)
  cacheTime?: number; // time in ms that cache is valid for globally (i.e. across component instances)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultCache?: Map<string, any>;
  method: TMethod;
}

const ABORT_MESSAGE = 'A new request needs to be made';

const DATA_TO_KEY_CACHE = new WeakMap<object, string>();

const GLOBAL_CACHE = new WeakMap<
  ApiRequest['method'],
  Map<string, { data: unknown; created: number }>
>();

// convert object to sorted string for predictability: { z: 'value', d: [5, 4, 3], a: ['z', 'f', 'a'] } => 'a=a,f,z&d=3,4,5&z=value'
function getNormalizedKey(payload: Record<string, unknown>) {
  return Object.entries(payload)
    .filter(([_, value]) => !isNullish(value))
    .sort(([keyA], [keyB]) => {
      return keyA < keyB ? -1 : 1;
    })
    .map(([key, value]) => {
      // @ts-ignore [toSorted](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/toSorted) is a non-mutating method
      return [key, Array.isArray(value) ? [...value].sort() : value];
    })
    .reduce((normalized, [key, value], index) => {
      return `${normalized}${!index ? '' : '&'}${key}=${value}`;
    }, '');
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function useRequest<TMethod extends (...args: any[]) => any>({
  abortable = true,
  autoRequestPayload,
  cacheable = false,
  cacheTime = 0,
  context = globalThis,
  defaultCache,
  method,
}: ApiRequest<TMethod>) {
  type MethodParams = Parameters<TMethod>[0];
  type MethodReturn = Awaited<ReturnType<TMethod>>;
  type Results = { data?: MethodReturn; error?: Error };

  const abortControllerRef = useRef<AbortController>(null);
  const cache = useRef(new Map<string, MethodReturn>(defaultCache as Map<string, MethodReturn>));
  const dataToKey = useRef(new WeakMap<object, string>());
  const [isPending, setIsPending] = useState(!!autoRequestPayload);
  const [results, setResults] = useState<Results>(EMPTY_OBJECT);
  const previousAutoRequestPayloadRef = useRef<MethodParams>();

  const wrappedSetResults = useCallback(
    (value: Parameters<typeof setResults>[0]) => {
      if (typeof value === 'function') {
        setResults((prev) => {
          const localNormalizedKey = dataToKey.current.get(prev.data as object);
          const globalNormalizedKey = DATA_TO_KEY_CACHE.get(prev.data as object);
          const updatedState = value(prev);

          if (localNormalizedKey) {
            cache.current.set(localNormalizedKey, updatedState.data as MethodReturn);
            dataToKey.current.set(updatedState.data as object, localNormalizedKey);
          }

          if (globalNormalizedKey) {
            GLOBAL_CACHE.get(method)?.set(globalNormalizedKey, {
              data: updatedState.data,
              created: Date.now(),
            });
            DATA_TO_KEY_CACHE.set(updatedState.data as object, globalNormalizedKey);
          }

          return updatedState;
        });

        return;
      }

      const localNormalizedKey = dataToKey.current.get(value.data as object);
      const globalNormalizedKey = DATA_TO_KEY_CACHE.get(value.data as object);

      if (localNormalizedKey) {
        cache.current.set(localNormalizedKey, value.data as MethodReturn);
        dataToKey.current.set(value.data as object, localNormalizedKey);
      }

      if (globalNormalizedKey) {
        GLOBAL_CACHE.get(method)?.set(globalNormalizedKey, {
          data: value.data,
          created: Date.now(),
        });
        DATA_TO_KEY_CACHE.set(value.data as object, globalNormalizedKey);
      }

      setResults(value);
    },
    [method],
  );

  const request = useCallback(
    async function (payload: MethodParams): Promise<Results> {
      // cacheable takes priority since it's designed for component "sessions" (i.e. only valid until the component unmounts)
      const normalizedKey = cacheable || cacheTime ? getNormalizedKey(payload) : '';
      let value: Results = EMPTY_OBJECT;

      try {
        let data = cache.current.get(normalizedKey) as MethodReturn;

        if (cacheTime) {
          // create key for individual methods
          let methodCache = GLOBAL_CACHE.get(method);
          if (!methodCache) {
            methodCache = new Map();
            GLOBAL_CACHE.set(method, methodCache);
          }

          // clear stale entries
          methodCache.forEach((value, key) => {
            if (Date.now() - value.created > cacheTime) {
              methodCache?.delete(key);
            }
          });

          const cachedData = methodCache.get(normalizedKey);
          if (cachedData) {
            data = cachedData.data as MethodReturn;
          }
        }

        if (!data) {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort(ABORT_MESSAGE);
          }

          const controller = new AbortController();
          abortControllerRef.current = abortable ? controller : null;

          setIsPending(true);

          data = await method.call(context, payload, {
            signal: controller.signal,
          });

          const isObject = typeof data === 'object' && data !== null;
          if (cacheable) {
            cache.current.set(normalizedKey, data);

            if (isObject) {
              dataToKey.current.set(data as object, normalizedKey);
            }
          }

          if (cacheTime) {
            GLOBAL_CACHE?.get(method)?.set(normalizedKey, { data, created: Date.now() });

            if (isObject) {
              DATA_TO_KEY_CACHE.set(data as object, normalizedKey);
            }
          }
        }

        value = { data };
      } catch (ex) {
        if (ex !== ABORT_MESSAGE) {
          console.error(ex);
          value = { error: ex instanceof Error ? ex : new Error(String(ex)) };
        }
      } finally {
        setIsPending(false);
        setResults(value);
      }

      return value;
    },
    [abortable, cacheable, cacheTime, context, method],
  );

  useEffect(() => {
    if (!autoRequestPayload || isEqual(previousAutoRequestPayloadRef.current, autoRequestPayload)) {
      return;
    }
    (async () => {
      previousAutoRequestPayloadRef.current = autoRequestPayload;
      await request(autoRequestPayload);
    })();
  }, [autoRequestPayload, request]);

  return { isPending, request, results, setResults: wrappedSetResults };
}
